import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";

import type { CommunityPillarDto, CommunityPillarProjectDto, PortfolioFeelingAtHomeProjectDto } from "@/api/types";
import chevronRightIcon from "@/assets/icons/chevron-right.svg";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Link } from "@/design-system/Link";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Skeleton } from "@/design-system/Skeleton";
import { Stack } from "@/design-system/Stack";
import { SUPPORT_EMAIL } from "@/helpers/constants";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { isDefined } from "@/helpers/util";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { Bar, BarChart, LabelList, XAxis, YAxis } from "@/ui-adapters/Recharts";
import { RuntimeHeight } from "@/ui-adapters/RuntimeHeight";

import { TrendBadge } from "../Badge/TrendBadge";
import { ArrowUpRightIcon } from "../Icons/ArrowUpRightIcon";
import { ChevronDownIcon } from "../Icons/ChevronDownIcon";
import { ChevronUpIcon } from "../Icons/ChevronUpIcon";
import { AnalyticsTrendGraphic } from "./AnalyticsTrendGraphic";
import { AnalyticsWidgetCard } from "./AnalyticsWidgetCard";
import { FahWidgetTrendChart } from "./FahWidgetTrendChart";
import {
  ceilingToOneDecimal,
  derivePrimaryDateRangeFromPeriod,
  deriveSecondaryDateRangeFromPeriod,
  formatAnalyticsDatePayload,
  formatAnalyticsRawValue,
} from "./helpers";
import type { AnalyticsWidgetBaseProps } from "./types";
import { useMetricHelp } from "./useMetricHelp";

const MAX_PREVIEW_PROJECTS = 6;

type FahWidgetProps = AnalyticsWidgetBaseProps;

export function FahWidget({ projectIds = [], period }: FahWidgetProps): React.ReactNode {
  const [projectsVisibility, setProjectsVisibility] = useState<"expanded" | "collapsed">("collapsed");
  const [selectedProjectSegment, setSelectedProjectSegment] = useState<"positive" | "negative">("positive");

  const { t } = useTranslation();
  const help = useMetricHelp();
  const fahTooltip = help("component.analytics-widget.feeling-at-home.tooltip");
  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();
  const analyticsQueries = useAnalyticsQueries();
  const linkedProjects = useLinkedProjects();
  const slug = useSlug();

  const { data: fahRecentStats, isPending: isPendingFahRecentStats } = useQuery({
    ...analyticsQueries.fah(projectIds[0]),
    enabled: projectIds.length === 1,
  });
  const secondaryDates = deriveSecondaryDateRangeFromPeriod(period);
  const secondaryDataQuery = {
    projectId: projectIds.length === 1 ? projectIds[0] : undefined,
    startDate: formatAnalyticsDatePayload(secondaryDates.startDate),
    endDate: formatAnalyticsDatePayload(secondaryDates.endDate),
  };
  const { data: secondaryFahAnalytics } = useQuery({
    ...portfolioAnalyticsQueries.fah(secondaryDataQuery),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });
  const { data: secondaryCommunityPillarAnalytics } = useQuery({
    ...portfolioAnalyticsQueries.communityPillars(secondaryDataQuery),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });
  const { data: fahHistoricStats, isLoading: isLoadingFahHistoricStats } = useQuery({
    ...analyticsQueries.fahMonthlyStats("last6Months", projectIds[0]),
    enabled: projectIds.length === 1,
  });

  const primaryDates = derivePrimaryDateRangeFromPeriod(period);
  const primaryDataQuery = {
    projectId: projectIds.length === 1 ? projectIds[0] : undefined,
    startDate: primaryDates.startDate ? formatAnalyticsDatePayload(primaryDates.startDate) : undefined,
    endDate: primaryDates.endDate ? formatAnalyticsDatePayload(primaryDates.endDate) : undefined,
  };
  const { data: primaryFahAnalytics, isPending: isPendingPrimaryFahAnalytics } = useQuery({
    ...portfolioAnalyticsQueries.fah(primaryDataQuery),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });
  const { data: primaryCommunityPillarAnalytics, isPending: isPendingPrimaryCommunityPillarAnalytics } = useQuery({
    ...portfolioAnalyticsQueries.communityPillars(primaryDataQuery),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const handleToggleProjectListVisibility = () => {
    if (projectsVisibility === "collapsed") {
      setProjectsVisibility("expanded");
    } else {
      document.querySelector("#portfolio-fah-widget")?.scrollIntoView({ behavior: "smooth" });
      setProjectsVisibility("collapsed");
    }
  };

  const aggregateData = (
    fahProjects: PortfolioFeelingAtHomeProjectDto[],
    fahPillarProjects: CommunityPillarProjectDto[],
  ) => {
    // Create mapping of projectId to CommunityPillarProjectDto for quick lookup
    const fahPillarProjectsByProjectId = new Map<string, CommunityPillarProjectDto>();
    for (const fahPillarProject of fahPillarProjects) {
      fahPillarProjectsByProjectId.set(fahPillarProject.projectId, fahPillarProject);
    }

    const listEligibleProjects = fahProjects.map((p) => {
      const pillars = fahPillarProjectsByProjectId.get(p.projectId)?.pillars ?? [];

      let servicePillar: CommunityPillarDto = { type: "service", score: 0, priority: 0, responseCount: 0 };
      let communityPillar: CommunityPillarDto = { type: "community", score: 0, priority: 0, responseCount: 0 };
      let maintenancePillar: CommunityPillarDto = { type: "maintenance", score: 0, priority: 0, responseCount: 0 };

      for (const pillar of pillars) {
        if (pillar.type === "service") servicePillar = pillar;
        if (pillar.type === "community") communityPillar = pillar;
        if (pillar.type === "maintenance") maintenancePillar = pillar;
      }

      return {
        ...p,
        servicePillar,
        communityPillar,
        maintenancePillar,
      };
    });

    return listEligibleProjects.reduce(
      (acc, project) => {
        acc.totalFahScore += project.feelAtHomeScore * project.responseCount;
        acc.totalFahResponseCount += project.responseCount;

        acc.totalServicePillarScore += project.servicePillar.score * project.servicePillar.responseCount;
        acc.totalServicePillarPriority += project.servicePillar.priority * project.servicePillar.responseCount;
        acc.totalServicePillarResponseCount += project.servicePillar.responseCount;

        acc.totalCommunityPillarScore += project.communityPillar.score * project.communityPillar.responseCount;
        acc.totalCommunityPillarPriority += project.communityPillar.priority * project.communityPillar.responseCount;
        acc.totalCommunityPillarResponseCount += project.communityPillar.responseCount;

        acc.totalMaintenancePillarScore += project.maintenancePillar.score * project.maintenancePillar.responseCount;
        acc.totalMaintenancePillarPriority +=
          project.maintenancePillar.priority * project.maintenancePillar.responseCount;
        acc.totalMaintenancePillarResponseCount += project.maintenancePillar.responseCount;

        acc.amountEligibleProjects += 1;

        return acc;
      },
      {
        totalFahScore: 0,
        totalFahResponseCount: 0,
        totalServicePillarScore: 0,
        totalServicePillarResponseCount: 0,
        totalServicePillarPriority: 0,
        totalCommunityPillarScore: 0,
        totalCommunityPillarResponseCount: 0,
        totalCommunityPillarPriority: 0,
        totalMaintenancePillarScore: 0,
        totalMaintenancePillarResponseCount: 0,
        totalMaintenancePillarPriority: 0,
        amountEligibleProjects: 0,
      },
    );
  };

  // Weighted average based on response count, ceiling'd to match backend
  const computeScore = (totalScore: number, totalResponseCount: number) => {
    if (totalResponseCount === 0) return 0;

    return ceilingToOneDecimal(totalScore / totalResponseCount);
  };

  const getDetailsLink = () => {
    let urlFahDetails: string = routes.surveys.fah({ slug });

    // Keep the direct survey fallback for projects without automatic details.
    if (!fahRecentStats?.hasFeelAtHomeDetailsLink && fahRecentStats?.manualSurveyId) {
      urlFahDetails = routes.surveys.details({ slug, id: fahRecentStats.manualSurveyId });
    }

    return urlFahDetails;
  };

  const primaryAggregatedData = aggregateData(
    primaryFahAnalytics?.projects ?? [],
    primaryCommunityPillarAnalytics?.projects ?? [],
  );
  const secondaryAggregatedData = aggregateData(
    secondaryFahAnalytics?.projects ?? [],
    secondaryCommunityPillarAnalytics?.projects ?? [],
  );
  const primaryAvgFahScore = computeScore(
    primaryAggregatedData.totalFahScore,
    primaryAggregatedData.totalFahResponseCount,
  );
  const secondaryAvgFahScore = computeScore(
    secondaryAggregatedData.totalFahScore,
    secondaryAggregatedData.totalFahResponseCount,
  );
  const primaryAvgServicePillarScore = computeScore(
    primaryAggregatedData.totalServicePillarScore,
    primaryAggregatedData.totalServicePillarResponseCount,
  );
  const secondaryAvgServicePillarScore = computeScore(
    secondaryAggregatedData.totalServicePillarScore,
    secondaryAggregatedData.totalServicePillarResponseCount,
  );
  const primaryAvgCommunityPillarScore = computeScore(
    primaryAggregatedData.totalCommunityPillarScore,
    primaryAggregatedData.totalCommunityPillarResponseCount,
  );
  const secondaryAvgCommunityPillarScore = computeScore(
    secondaryAggregatedData.totalCommunityPillarScore,
    secondaryAggregatedData.totalCommunityPillarResponseCount,
  );
  const primaryAvgMaintenancePillarScore = computeScore(
    primaryAggregatedData.totalMaintenancePillarScore,
    primaryAggregatedData.totalMaintenancePillarResponseCount,
  );
  const secondaryAvgMaintenancePillarScore = computeScore(
    secondaryAggregatedData.totalMaintenancePillarScore,
    secondaryAggregatedData.totalMaintenancePillarResponseCount,
  );
  let sortedProjects: PortfolioFeelingAtHomeProjectDto[] = new Array<PortfolioFeelingAtHomeProjectDto>(
    MAX_PREVIEW_PROJECTS,
  ).fill({
    projectId: "",
    projectName: "",
    projectSlug: "",
    projectLogo: {
      url: "",
      id: "",
      metadata: {},
      publicId: "",
    },
    feelAtHomeScore: 0,
    responseCount: 0,
  });
  if (primaryFahAnalytics && primaryFahAnalytics.projects.length > 0) {
    sortedProjects = primaryFahAnalytics.projects.toSorted((a, b) => {
      if (selectedProjectSegment === "positive") {
        return b.feelAtHomeScore - a.feelAtHomeScore;
      }

      return a.feelAtHomeScore - b.feelAtHomeScore;
    });
  } else if (linkedProjects.data) {
    sortedProjects = linkedProjects.data
      .filter((project) => projectIds.length === 0 || projectIds.includes(project.id))
      .filter((project) => project.userRole !== "resident")
      .map((project) => ({
        projectId: project.id,
        projectName: project.name,
        projectSlug: project.slug,
        projectLogo: {
          url: project.logoUrl || "",
          id: "",
          metadata: {},
          publicId: "",
        },
        feelAtHomeScore: 0,
        responseCount: 0,
      }));
  }

  const amountProjectsInScope = sortedProjects.length;
  const amountDisplayedProjects = projectsVisibility === "collapsed" ? MAX_PREVIEW_PROJECTS : amountProjectsInScope;
  const communityPillarScoresByType: Record<CommunityPillarDto["type"], number> = {
    service: primaryAvgServicePillarScore,
    community: primaryAvgCommunityPillarScore,
    maintenance: primaryAvgMaintenancePillarScore,
  };
  const residentPriorityPillar = Object.entries(communityPillarScoresByType).reduce<
    CommunityPillarDto["type"] | undefined
  >((currPriorityPillarType, currPillar) => {
    const [currPillarType, currPillarScore] = currPillar as [CommunityPillarDto["type"], number];

    const currPriorityPillarScore = currPriorityPillarType
      ? communityPillarScoresByType[currPriorityPillarType]
      : undefined;

    // If no priority pillar has been set yet
    if (!currPriorityPillarType) {
      if (currPillarScore !== 0) {
        return currPillarType;
      }

      return undefined;
    }

    // The lower the priority value, the higher the priority
    if (isDefined(currPriorityPillarScore) && currPillarScore < currPriorityPillarScore) {
      return currPillarType;
    }

    // In case of a tie, community pillar takes precedence
    if (currPillarScore === communityPillarScoresByType[currPriorityPillarType] && currPillarType === "community") {
      return "community";
    }

    return currPriorityPillarType;
  }, undefined);

  const chartData = sortedProjects.slice(0, amountDisplayedProjects).map((project) => ({
    name: project.projectName,
    score: project.feelAtHomeScore,
    metadata: {
      name: project.projectName,
      id: project.projectId,
      slug: project.projectSlug,
    },
  }));

  const isFahNotStarted =
    projectIds.length === 1 && !isPendingFahRecentStats && fahRecentStats?.status === "notStarted";

  // Always show data if only one project is in scope, unless the survey hasn't started yet for that project
  const isDataAvailable =
    !isFahNotStarted && (amountProjectsInScope === 1 || primaryAggregatedData.amountEligibleProjects > 0);
  const isPending =
    isPendingPrimaryFahAnalytics ||
    isPendingPrimaryCommunityPillarAnalytics ||
    isLoadingFahHistoricStats ||
    (projectIds.length === 1 ? isPendingFahRecentStats : projectIds.length === 0 && isPendingFahRecentStats);

  return (
    <AnalyticsWidgetCard
      id="portfolio-fah-widget"
      isLoading={isPending}
      renderSkeleton={() => <WidgetSkeleton />}
      renderEmptyState={() => <WidgetEmpty isNotStarted={isFahNotStarted} />}
      isDataAvailable={isDataAvailable}
    >
      <Stack gap={{ base: "md", container: { md: "xl" } }}>
        <section className="flex flex-wrap items-end gap-space-md divide-border-default *:pr-space-md @xl:gap-space-lg @xl:divide-x @xl:pr-space-lg">
          <section className="group/fah-score flex min-w-analytics-score flex-1 flex-col justify-end gap-space-lg">
            <Inline gap="sm" align="center" wrap={false}>
              {fahTooltip && <Info tooltip={fahTooltip} />}
              {amountProjectsInScope > 1 ? (
                <h3 className="text-heading-3">{t("component.analytics-widget.feeling-at-home.fah.title")}</h3>
              ) : (
                <Link variant="heading" href={getDetailsLink()} target="_blank" underline="none">
                  <Inline as="span" gap="xs" align="center" wrap={false}>
                    <span>{t("component.analytics-widget.feeling-at-home.fah.title")}</span>
                    <ArrowUpRightIcon size={12} tone="brand" />
                  </Inline>
                </Link>
              )}
            </Inline>
            <Inline gap="md" align="baseline" wrap={false}>
              <span className="text-figure-xl text-text-brand-secondary">
                {primaryAvgFahScore ? formatAnalyticsRawValue(primaryAvgFahScore) : "-"}
              </span>
              {Boolean(primaryAvgFahScore) && Boolean(secondaryAvgFahScore) && (
                <TrendBadge value={primaryAvgFahScore} referenceValue={secondaryAvgFahScore} isZeroOmitted />
              )}
            </Inline>
          </section>
          <FahPillar
            isPriority={residentPriorityPillar === "service"}
            title={t("component.analytics-widget.feeling-at-home.service.title")}
            score={communityPillarScoresByType["service"]}
            referenceScore={secondaryAvgServicePillarScore}
          />
          <FahPillar
            isPriority={residentPriorityPillar === "community"}
            title={t("component.analytics-widget.feeling-at-home.community.title")}
            score={communityPillarScoresByType["community"]}
            referenceScore={secondaryAvgCommunityPillarScore}
          />
          <FahPillar
            isPriority={residentPriorityPillar === "maintenance"}
            title={t("component.analytics-widget.feeling-at-home.maintenance.title")}
            score={communityPillarScoresByType["maintenance"]}
            referenceScore={secondaryAvgMaintenancePillarScore}
          />
        </section>
        {amountProjectsInScope > 1 && (
          <Inline gap="md" align="center" justify="between">
            <h4 className="text-heading-4">{t("component.analytics-widget.feeling-at-home.subtitle")}</h4>
            <div className="w-full @md:w-measure-compact">
              <SegmentGroup
                value={selectedProjectSegment}
                onChange={(value) => setSelectedProjectSegment(value)}
                items={[
                  {
                    label: t("component.analytics-widget.project-segment-group.label.top-performing"),
                    value: "positive",
                  },
                  {
                    label: t("component.analytics-widget.project-segment-group.label.bottom-performing"),
                    value: "negative",
                  },
                ]}
              />
            </div>
            <RuntimeHeight height={chartData.length * 60} minHeight={80}>
              {(heightAttributes) => (
                <BarChart
                  margin={{ top: 0, right: 0, left: 8, bottom: 0 }}
                  {...heightAttributes}
                  layout="vertical"
                  data={chartData}
                  className="-ml-space-xs w-full"
                  responsive
                >
                  <XAxis
                    type="number"
                    dataKey="score"
                    domain={[0, 10]}
                    tickCount={11}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: "var(--text-overline)" }}
                    tickMargin={8}
                  />
                  <YAxis type="category" dataKey="name" hide />
                  <Bar
                    dataKey="score"
                    barSize={8}
                    maxBarSize={8}
                    radius={2}
                    // Recharts will skip rendering if value is 0
                    minPointSize={Number.MIN_VALUE}
                    className="fill-background-brand-secondary"
                    isAnimationActive={false}
                    background={<BarBackground />}
                  >
                    <LabelList
                      dataKey="metadata"
                      content={({ x, y, height, value }) => {
                        if (
                          !value ||
                          !x ||
                          !y ||
                          !height ||
                          typeof x !== "number" ||
                          typeof y !== "number" ||
                          typeof height !== "number" ||
                          typeof value !== "object"
                        ) {
                          return null;
                        }

                        // For type safety reasons (even though Rechart does not support object as data source)
                        const currMetadata = value as { name: string; id: string; slug: string };

                        return (
                          <g>
                            <a
                              href={routes.home.home({ slug: currMetadata.slug })}
                              className="*:transition-colors hover:[&>svg]:text-text-brand! hover:[&>text]:fill-text-brand!"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <text
                                x={x}
                                y={y - 2.5 - 4}
                                textAnchor="start"
                                className="fill-text-primary text-caption"
                                ref={(node) => {
                                  if (!node) return;

                                  const icon = node.nextSibling;

                                  // Manually set the link icon's x position based on the text width
                                  if (icon instanceof SVGElement) {
                                    const width = node.getBBox().width;

                                    icon.setAttribute("x", String(x + width + 4));
                                  }
                                }}
                              >
                                {currMetadata.name}
                              </text>
                              <svg
                                x={x}
                                y={y - 12 - 4 - 0.5}
                                width={12}
                                height={12}
                                className="fill-transparent"
                                color="var(--color-text-secondary)"
                              >
                                <use xlinkHref={`#${chevronRightIcon}`} />
                              </svg>
                            </a>
                          </g>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              )}
            </RuntimeHeight>
            {amountProjectsInScope > MAX_PREVIEW_PROJECTS && (
              <Button
                variant="ghostSecondary"
                icon={projectsVisibility === "collapsed" ? <ChevronDownIcon /> : <ChevronUpIcon />}
                onClick={handleToggleProjectListVisibility}
                size="sm"
              >
                {projectsVisibility === "collapsed"
                  ? t("component.analytics-widget.feeling-at-home.btn.show-all-projects", { count: projectIds.length })
                  : t("component.analytics-widget.feeling-at-home.btn.show-less")}
              </Button>
            )}
          </Inline>
        )}
        {amountProjectsInScope === 1 && (
          <figure className="relative w-full overflow-hidden">
            <FahWidgetTrendChart data={fahHistoricStats || []} />
          </figure>
        )}
      </Stack>
    </AnalyticsWidgetCard>
  );
}

function BarBackground({
  x,
  y,
  width,
  height,
  onClick,
  onMouseEnter,
  onMouseLeave,
  fill,
}: React.SVGProps<SVGRectElement>): React.ReactNode {
  return (
    <rect
      className="fill-background-secondary"
      rx={2}
      {...{ x, y, width, height, onClick, onMouseEnter, onMouseLeave, fill }}
    />
  );
}

function FahPillar({
  title,
  score,
  referenceScore,
  isPriority,
}: {
  title: string;
  score?: number;
  referenceScore?: number;
  isPriority: boolean;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <section className="flex min-w-analytics-score flex-1 flex-col justify-end gap-space-sm">
      {isPriority && (
        <Badge variant="special">{t("component.analytics-widget.feeling-at-home.badge.resident-priority")}</Badge>
      )}
      <Stack gap="md">
        <span className="text-body-emphasis">{title}</span>
        <Inline gap="md" align="baseline" wrap={false}>
          <span className="text-figure-md text-text-brand-secondary">
            {score ? formatAnalyticsRawValue(score) : "-"}
          </span>
          {Boolean(score) && Boolean(referenceScore) && (
            <>
              <TrendBadge value={score!} referenceValue={referenceScore} isZeroOmitted />
              <span className="ml-auto">
                <AnalyticsTrendGraphic value={score!} referenceValue={referenceScore} />
              </span>
            </>
          )}
        </Inline>
      </Stack>
    </section>
  );
}

function WidgetSkeleton(): React.ReactNode {
  return (
    <section className="flex size-full flex-col gap-space-xl">
      <ul className="flex w-full justify-between divide-border-default @xl:divide-x">
        {new Array(4).fill(null).map((_, index) => (
          <li key={index} className="flex-1 px-space-md first:pl-0 last:pr-0">
            <Skeleton height="28" />
          </li>
        ))}
      </ul>
      <div className="flex-1">
        <Skeleton />
      </div>
    </section>
  );
}

function WidgetEmpty({ isNotStarted }: { isNotStarted: boolean }): React.ReactNode {
  const { t } = useTranslation();

  if (isNotStarted) {
    return (
      <section className="flex size-full flex-col items-center justify-center gap-space-md p-space-md text-center">
        <Stack gap="xs">
          <h3 className="text-heading-3">{t("component.analytics-widget.fah.empty.title")}</h3>
          <p className="text-text-secondary">{t("component.analytics-widget.fah.empty.description")}</p>
        </Stack>
        <Button type="link" href={`mailto:${SUPPORT_EMAIL}?subject=Request to start FAH survey`} isExternal>
          {t("component.analytics-widget.fah.get-help-success.btn")}
        </Button>
      </section>
    );
  }

  return (
    <section className="flex size-full flex-col items-center justify-center gap-space-md">
      <span className="text-heading-3">{t("component.analytics-widget.feeling-at-home.empty-state.title")}</span>
    </section>
  );
}
